// tagAssign.js — wall tags → perimeter edges. Pure; runs in the worker AND on the main thread so the
// estimator can switch tags on/off without re-running the geometry.
//
// Rule (the estimator's rule): a tag anywhere along a straight wall line applies to that whole line.
// If one line carries different tags, each edge takes the nearest tag along the line.
// sideAware = finish tags (P-1 paint etc.) that belong to ONE face: only tags on the room's side count.

/**
 * @param {object} p
 * @param {Array} p.edges     [{a:[x,y], b:[x,y], kind:'wall'|'window'|'door'|'opening', run, lf}] (sheet units)
 * @param {Array} p.runs      [{id, ux, uy, nx, ny, c}]
 * @param {Array} p.tagCands  [{text, run, t, side}]
 * @param {Set<string>|null} p.enabled  tag texts that count (null = all)
 * @param {boolean} p.sideAware
 * @param {number}  [p.slack]  extra centre-line distance (sheet units) for runs to count as one line
 */
export function assignTags({ edges, runs, tagCands, enabled = null, sideAware = false, slack = null }) {
  // A wall LINE = every run on the same straight line (same direction, centre lines within a wall
  // thickness), however it is broken up — by doors, jogs, a change of wall thickness, a window or an
  // intersection. A tag anywhere on the line applies to the whole line; along a line that carries
  // different tags, each edge takes the nearest one. Better one wall too many than one missing.
  const lineOf = groupLines(runs, slack);
  const byLine = new Map();
  for (const t of tagCands || []) {
    if (enabled && !enabled.has(t.text)) continue;
    const R = runs[t.run]; if (!R) continue;
    const g = lineOf[t.run];
    if (!byLine.has(g)) byLine.set(g, []);
    byLine.get(g).push({ ...t, tt: R.ux * t.x + R.uy * t.y });
  }
  return edges.map((e) => {
    // a window sits IN its wall: it carries the wall's tag (the board runs above and below it)
    if (e.kind !== "wall" && e.kind !== "window") return { ...e, tag: null };
    const run = e.run >= 0 && runs[e.run] ? e.run : nearestRun(e, runs);
    if (run < 0) return { ...e, tag: null };
    let list = byLine.get(lineOf[run]);
    const R = runs[run];
    if (!list?.length || !R) return { ...e, tag: null };
    const mx = (e.a[0] + e.b[0]) / 2, my = (e.a[1] + e.b[1]) / 2;
    if (sideAware) {
      const side = Math.sign(R.nx * mx + R.ny * my - R.c) || 1;
      list = list.filter((t) => (Math.sign(R.nx * t.x + R.ny * t.y - R.c) || t.side) === side);
      if (!list.length) return { ...e, tag: null };
    }
    const tm = R.ux * mx + R.uy * my;
    let best = list[0];
    for (const t of list) if (Math.abs(t.tt - tm) < Math.abs(best.tt - tm)) best = t;
    return { ...e, run, tag: best.text };
  });
}

// union runs lying on one straight line → line id per run
function groupLines(runs, slack) {
  const p = runs.map((_, i) => i);
  const f = (x) => { while (p[x] !== x) x = p[x] = p[p[x]]; return x; };
  const ths = runs.map((r) => r.th || 0).filter(Boolean).sort((a, b) => a - b);
  const typ = ths.length ? ths[ths.length >> 1] : 0;
  const sl = slack ?? typ * 0.6;
  // bucket by direction so this stays fast on busy sheets
  const byDir = new Map();
  runs.forEach((r, i) => { const a = Math.round(((Math.atan2(r.uy, r.ux) + Math.PI) % Math.PI) * 180 / Math.PI) % 180; for (const k of [a, (a + 1) % 180, (a + 179) % 180]) { if (!byDir.has(k)) byDir.set(k, []); byDir.get(k).push(i); } });
  for (const ids of byDir.values()) {
    for (let x = 0; x < ids.length; x++) for (let y = x + 1; y < ids.length; y++) {
      const A = runs[ids[x]], B = runs[ids[y]];
      if (Math.abs(A.ux * B.ux + A.uy * B.uy) < 0.9995) continue;
      const sgn = A.nx * B.nx + A.ny * B.ny > 0 ? 1 : -1;      // normals may point opposite ways
      const d = Math.abs(A.c - sgn * B.c);
      if (d <= Math.max(A.th || typ, B.th || typ) / 2 + sl) p[f(ids[x])] = f(ids[y]);
    }
  }
  return runs.map((_, i) => f(i));
}

// an edge the engine couldn't tie to a wall run: the parallel run whose band it lies against
function nearestRun(e, runs) {
  const len = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]) || 1;
  const ux = (e.b[0] - e.a[0]) / len, uy = (e.b[1] - e.a[1]) / len;
  const mx = (e.a[0] + e.b[0]) / 2, my = (e.a[1] + e.b[1]) / 2;
  let best = -1, bd = Infinity;
  runs.forEach((R, i) => {
    if (Math.abs(ux * R.ux + uy * R.uy) < 0.998) return;
    const d = Math.abs(R.nx * mx + R.ny * my - R.c), t = R.ux * mx + R.uy * my, th = R.th || 0;
    if (R.t0 !== undefined && (t < R.t0 - th - len * 0.5 || t > R.t1 + th + len * 0.5)) return;
    if (d <= th * 1.6 + 1 && d < bd) { bd = d; best = i; }
  });
  return best;
}

/**
 * Group consecutive edges with the same tag into polylines (so one wall run = one shape).
 * Doors and plain openings break the run (no board there). Windows either break it too (windowsBreak) or
 * stay inside it (board above/below) and are listed for an area deduction.
 * → [{tag, pts, lf, windows:[lf…], runLf:{run: lf}, run}]  (run = the run carrying most of the chain)
 */
export function chainEdges(edges, { windowsBreak = false } = {}) {
  const out = [];
  // windowsBreak: a window is left out of the wall line (like a door) instead of staying in it
  const isWall = (e) => e.kind === "wall" || (!windowsBreak && e.kind === "window");
  for (const e of edges) {
    if (!isWall(e)) { out.push(null); continue; }   // a break
    const prev = out[out.length - 1];
    const join = prev && prev.tag === e.tag && Math.hypot(prev.pts[prev.pts.length - 1][0] - e.a[0], prev.pts[prev.pts.length - 1][1] - e.a[1]) < 1e-3;
    if (join) {
      prev.pts.push(e.b); prev.lf += e.lf;
      if (e.kind === "window") {
        // touching window pieces are one window
        const lw = prev.windowSegs[prev.windowSegs.length - 1];
        if (lw && Math.hypot(lw[1][0] - e.a[0], lw[1][1] - e.a[1]) < 1e-3) { lw[1] = e.b; prev.windows[prev.windows.length - 1] += e.lf; }
        else { prev.windows.push(e.lf); prev.windowSegs.push([e.a, e.b]); }
      }
      if (e.run >= 0) prev.runLf[e.run] = (prev.runLf[e.run] || 0) + e.lf;
    } else out.push({ tag: e.tag, pts: [e.a, e.b], lf: e.lf, windows: e.kind === "window" ? [e.lf] : [], windowSegs: e.kind === "window" ? [[e.a, e.b]] : [], runLf: e.run >= 0 ? { [e.run]: e.lf } : {} });
  }
  // the ring wraps: merge last chain into first when they meet with the same tag
  const first = out[0], last = out[out.length - 1];
  const res = out.filter(Boolean);
  if (res.length > 1 && first && last && first !== last) {
    const lp = last.pts[last.pts.length - 1];
    if (first.tag === last.tag && Math.hypot(lp[0] - first.pts[0][0], lp[1] - first.pts[0][1]) < 1e-3) {
      first.pts = [...last.pts, ...first.pts.slice(1)]; first.lf += last.lf; first.windows = [...last.windows, ...first.windows]; first.windowSegs = [...last.windowSegs, ...first.windowSegs];
      for (const [k, v] of Object.entries(last.runLf)) first.runLf[k] = (first.runLf[k] || 0) + v;
      res.pop();
    }
  }
  // consecutive window pieces of one opening come out as separate bits — merge touching ones
  for (const c of res) {
    const keep = c.windows.map((x, i) => (x >= 0.5 ? i : -1)).filter((i) => i >= 0);
    c.windowSegs = keep.map((i) => c.windowSegs[i]);
    c.windows = keep.map((i) => c.windows[i]);
    let bestRun = -1, bl = 0;
    for (const [k, v] of Object.entries(c.runLf)) if (v > bl) { bl = v; bestRun = Number(k); }
    c.run = bestRun;
  }
  return res;
}

/**
 * Tagged wall faces that no closed room drew (open-plan areas that leak, corridors, lobbies).
 * faces: wallFaces() output; roomEdges: every closed room's edges (after assignTags).
 * Returns [{tag, run, side, a, b, len}] in sheet units — only faces whose wall LINE carries a tag.
 */
export function uncoveredTaggedFaces({ faces, roomEdges, runs, tagCands, enabled = null, minLen = 0 }) {
  const cov = new Map();   // run|side → [[t0,t1]]
  for (const e of roomEdges || []) {
    if (e.run < 0 || !runs[e.run]) continue;
    const R = runs[e.run];
    const mx = (e.a[0] + e.b[0]) / 2, my = (e.a[1] + e.b[1]) / 2;
    const side = Math.sign(R.nx * mx + R.ny * my - R.c) || 1;
    const ta = R.ux * e.a[0] + R.uy * e.a[1], tb = R.ux * e.b[0] + R.uy * e.b[1];
    const k = `${e.run}|${side}`;
    if (!cov.has(k)) cov.set(k, []);
    cov.get(k).push([Math.min(ta, tb) - (R.th || 0), Math.max(ta, tb) + (R.th || 0)]);
  }
  const out = [];
  for (const f of faces || []) {
    const R = runs[f.run]; if (!R) continue;
    // subtract covered stretches
    let pieces = [[f.t0, f.t1]];
    for (const [c0, c1] of cov.get(`${f.run}|${f.side}`) || []) {
      const next = [];
      for (const [p0, p1] of pieces) { if (c1 <= p0 || c0 >= p1) next.push([p0, p1]); else { if (c0 > p0) next.push([p0, c0]); if (c1 < p1) next.push([c1, p1]); } }
      pieces = next;
    }
    const off = R.c + f.side * (R.th || 0) / 2;
    for (const [p0, p1] of pieces) {
      if (p1 - p0 < minLen) continue;
      const a = [R.ux * p0 + R.nx * off, R.uy * p0 + R.ny * off], b = [R.ux * p1 + R.nx * off, R.uy * p1 + R.ny * off];
      const [e] = assignTags({ edges: [{ a, b, kind: "wall", run: f.run, lf: 0 }], runs, tagCands, enabled });
      if (e.tag) out.push({ tag: e.tag, run: f.run, side: f.side, a, b, len: p1 - p0 });
    }
  }
  return out;
}

/**
 * Most likely wall type for walls with no tag on their line: the type the drafter uses for walls of the
 * same thickness on this sheet. Only when one type clearly dominates that thickness (≥ 60 % of the tagged
 * LF, at least `minLf` of it); otherwise null → "Untagged wall" for the estimator to assign.
 * chains: [{tag, run, lf}] (all rooms), runs: [{th}] sheet units, inPerUnit: inches per sheet unit
 * → (chain) => { tag, share } | null
 */
export function thicknessGuesser(chains, runs, inPerUnit, minLf = 8) {
  const key = (run) => (run >= 0 && runs[run]?.th ? Math.round(runs[run].th * inPerUnit * 2) / 2 : null);
  const byTh = new Map();
  for (const c of chains) {
    if (!c.tag) continue;
    const k = key(c.run); if (k === null) continue;
    if (!byTh.has(k)) byTh.set(k, new Map());
    const m = byTh.get(k); m.set(c.tag, (m.get(c.tag) || 0) + c.lf);
  }
  return (c) => {
    const k = key(c.run); if (k === null) return null;
    // the same thickness, or within ½" (drafting noise)
    const m = new Map();
    for (const [th, tags] of byTh) if (Math.abs(th - k) <= 0.5) for (const [t, lf] of tags) m.set(t, (m.get(t) || 0) + lf);
    let tot = 0, best = null, bl = 0;
    for (const [t, lf] of m) { tot += lf; if (lf > bl) { bl = lf; best = t; } }
    if (!best || bl < minLf || bl / tot < 0.6) return null;
    return { tag: best, share: bl / tot };
  };
}
